"use client";

import { VenetianMaskIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../atoms/button";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "../atoms/item";
import { Popover, PopoverContent, PopoverTrigger } from "../atoms/popover";
import { Separator } from "../atoms/separator";
import { cn } from "../lib";
import { UserSearchList, type UserSearchListUser } from "./user-search-list";

type User = {
  id: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
};

export function ImpersonationSelector({
  className,
  actualUser,
  effectiveUser,
  users,
  onImpersonate,
  ...rest
}: React.ComponentProps<"button"> & {
  actualUser?: User;
  effectiveUser?: User;
  /**
   * Who can be impersonated, in the order they should appear — the list is
   * shown as given, and searching preserves that order. Pass `initials` for
   * anyone with a short handle in the current context so it can be typed.
   */
  users: UserSearchListUser[];
  onImpersonate: (userId?: string) => Promise<void>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isImpersonating = actualUser?.id !== effectiveUser?.id;

  const handleImpersonate = async (userId?: string) => {
    setIsLoading(true);

    try {
      await onImpersonate(userId);
      setIsOpen(false);
    } catch (error) {
      console.error("Error impersonating user:", error);
      toast.error("Failed to impersonate user.", {
        description: "Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        render={
          <Button
            className={cn("relative rounded-full", className)}
            variant="nav"
            size="icon"
            {...rest}
          />
        }
      >
        <VenetianMaskIcon className="size-5" />
        {isImpersonating && (
          <div className="absolute top-1 right-1 size-2 bg-destructive rounded-full" />
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-screen rounded-none lg:rounded-md lg:w-96 p-0"
        align="end"
      >
        {isImpersonating && (
          <div className="bg-muted p-2">
            <div className="text-center text-xs text-muted-foreground">
              Currently viewing as
            </div>
            <Item size="sm">
              <ItemContent>
                <ItemTitle>{effectiveUser?.name}</ItemTitle>
                <ItemDescription>{effectiveUser?.email}</ItemDescription>
              </ItemContent>
              <ItemActions>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleImpersonate()}
                >
                  Stop
                </Button>
              </ItemActions>
            </Item>
          </div>
        )}
        <Separator />
        {users.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Loading users...
          </div>
        ) : (
          <UserSearchList
            users={users}
            onSelect={handleImpersonate}
            placeholder="Impersonate users..."
            disabled={isLoading}
            autoFocus
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
